/**
 * Platform checks and sharing for the studio, in PCake.
 *
 * PCake runs both as a website and as a Capacitor app, so the real Capacitor object is used for
 * the platform checks. Sharing goes through Capacitor's Share plugin in the native app and
 * through CreativeStudio's Web Share + download fallback in a browser.
 */
import { Capacitor } from '@capacitor/core';
import { Share as CapShare } from '@capacitor/share';

export { Capacitor };

export const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const webShare = {
  async canShare(data) {
    return typeof navigator.share === 'function' && (!data?.files || navigator.canShare?.({ files: data.files }));
  },
  async share({ title, text, url, files } = {}) {
    if (typeof navigator.share === 'function') {
      const payload = {};
      if (title) payload.title = title;
      if (text) payload.text = text;
      if (url) payload.url = url;
      if (files?.length && navigator.canShare?.({ files })) payload.files = files;
      try {
        await navigator.share(payload);
        return { activityType: 'web-share' };
      } catch (err) {
        if (err?.name === 'AbortError') return { activityType: 'cancelled' };
      }
    }
    if (url) {
      const a = document.createElement('a');
      a.href = url;
      a.download = (title || 'pcake').replace(/[^\w.-]+/g, '_');
      document.body.appendChild(a);
      a.click();
      a.remove();
      return { activityType: 'download' };
    }
    throw new Error('Sharing is not supported in this browser.');
  },
};

export const Share = {
  canShare: (data) => (Capacitor.isNativePlatform() ? CapShare.canShare() : webShare.canShare(data)),
  share: (opts) => (Capacitor.isNativePlatform() ? CapShare.share(opts) : webShare.share(opts)),
};
